import _ from "underscore";

/**
 * Returns true only if `o` is a plain JS object.
 * Cannot use Underscores _.isObject() in this case
 * as it returns true for all of the following:
 *     _.isObject(new Date());
 *     _.isObject([]);
 *     _.isObject(window)
 * @param {*} o
 * @returns {Boolean}
 */
function isObject(o) {
    return Object.prototype.toString.call(o) === "[object Object]";
}

/**
 * Calls the function `fn` with the object references by the the penultimate part of `keypath`.
 * @param {object} object
 * @param {string} keyPath A string representing a single key or multiple keys joined with a '.'
 * @param {function} fn A function that will be called with the eventual object and key.
 * @returns {*} The return value from `fn`.
 *
 * @example
 *     var foo = {
 *         bar: {
 *             baz: 5
 *         }
 *     };
 *
 *     access(foo, 'bar', function (obj, key) {
 *         // `obj` will be `foo` and key will be "bar"
 *     });
 *
 *     access(foo, 'bar.baz', function (obj, key) {
 *         // `obj` will be `foo.bar` and key will be "baz"
 *     });
 */
function access(object, keyPath, fn) {
    var parts = keyPath.split(".");
    var key = parts.pop();

    if (parts.length === 0) {
        return fn(object, key);
    }

    var curr = object;
    var currKey;

    while (curr && parts.length) {
        currKey = parts.shift();

        if (!(currKey in curr)) {
            return fn(undefined, key);
        }

        curr = curr[currKey];
    }

    return fn(curr, key);
}

/**
 * @constructor
 */
function Environment() {
    this._states = {};
    this._currState = {};
}

Environment.prototype = {
    /**
     * Sets new values.
     * @param {String|Object} key Either the key to set or an object to mix into values.
     * @param {*} [value] Value to set if passing a string as `key`.
     * @returns {Environment}
     */
    set: function(key, value) {
        if (isObject(key)) {
            // mix into root data
            _.extend(this._currState, key);
        } else if (_.isString(key)) {
            this._currState[key] = value;
        }

        return this;
    },

    /**
     * Gets a value.
     * @param {String} keyPath Path the the value to be retrieved.
     * @param {*} [defaultValue] If passed this will be returned
     *                           if the desired key path does not exist.
     * @returns {*}
     */
    get: function(keyPath, defaultValue) {
        defaultValue = arguments.length > 1 ? defaultValue : null;

        return access(this._currState, keyPath, function(obj, key) {
            if (_.has(obj, key)) {
                return obj[key];
            }

            return defaultValue;
        });
    },

    /**
     * Deletes the value at `keyPath`.
     * @param {string} keyPath
     * @returns {Environment}
     */
    delete: function(keyPath) {
        let env = this;
        return access(this._currState, keyPath, function(obj, key) {
            if (_.has(obj, key)) {
                delete obj[key];
            }
            return env;
        });
    },

    /**
     * Retrives the value at `keyPath`, then deletes it and returns it.
     * @param {string} keyPath
     * @returns {*}
     */
    pop: function(keyPath) {
        var value = this.get.apply(this, arguments);
        this.delete(keyPath);
        return value;
    },

    /**
     * Saves the current state under `key`. If there is already some state stored under `key`
     * then it will be overwritten.
     * @param {string} key
     */
    saveState: function(key) {
        if (!key || !_.isString(key)) {
            throw new Error("key must be a non-empty string " + typeof key);
        }

        // deep clone the current state and index at the given id
        this._states[key] = JSON.parse(JSON.stringify(this._currState));

        // set the current state to this object
        this._currState = this._states[key];
    },

    /**
     * Returns true if there is state saved with `key`.
     * @param {string} key
     * @returns {boolean}
     */
    hasState: function(key) {
        return this._states.hasOwnProperty(key);
    },

    /**
     * Loads the environment state saved with `key`. If there is no state saved with `key` then
     * an error will be thrown.
     * @param {string} key
     */
    loadState: function(key) {
        if (!key || !_.isString(key)) {
            throw new Error("key must be a non-empty string");
        }

        if (!this._states.hasOwnProperty(key)) {
            throw new Error("There is no envionment state indexed with key: " + key);
        }

        this._currState = this._states[key];
    },
};

var environment = new Environment();

// seed environment with data passed from server
var lyst = window.Lyst || {};
environment.set(lyst.data);
environment.set(lyst.environment);

// expose for testing
environment.Environment = Environment;

export default environment;
